import { CheckIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/utils/classname';

type Mode = 'query' | 'agent' | 'auto';

interface ModeSelectorProps {
  mode: Mode;
  onModeChange: (mode: Mode) => void;
  disabled?: boolean;
  showAuto?: boolean;
}

interface ModeOption {
  value: Mode;
  label: string;
  description: string;
}

const MODE_OPTIONS: ModeOption[] = [
  {
    value: 'query',
    label: 'Query',
    description: 'Human In the Loop',
  },
  {
    value: 'agent',
    label: 'Agent',
    description: 'Human On the Loop',
  },
  {
    value: 'auto',
    label: 'Auto',
    description: 'Human Off the Loop',
  },
];

export function ModeSelector({
  mode,
  onModeChange,
  disabled = false,
  showAuto = false,
}: ModeSelectorProps) {
  const [open, setOpen] = useState(false);

  const options = showAuto
    ? MODE_OPTIONS
    : MODE_OPTIONS.filter(o => o.value !== 'auto');

  const activeOption =
    MODE_OPTIONS.find(o => o.value === mode) ?? MODE_OPTIONS[0];

  const handleSelect = (value: Mode) => {
    onModeChange(value);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-tutorial="ai-mode-selector"
          onClick={e => {
            e.stopPropagation();
          }}
          disabled={disabled}
          className={cn(
            'group inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all duration-200',
            'border-chariot-brand-primary text-chariot-brand-primary',
            'hover:bg-chariot-brand-primary hover:text-white',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          <div
            className={cn(
              'h-1.5 w-1.5 rounded-full group-hover:bg-white',
              'bg-chariot-brand-primary'
            )}
          />
          <span className="capitalize">{activeOption.label}</span>
          <ChevronUpIcon className="size-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        className="w-56 p-1"
        style={{ zIndex: 100 }}
      >
        {options.map(option => (
          <button
            key={option.value}
            type="button"
            onClick={() => handleSelect(option.value)}
            className={cn(
              'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
              'hover:bg-chariot-background-secondary/50',
              mode === option.value && 'bg-chariot-background-secondary'
            )}
          >
            <div className="flex-1 text-left">
              <div className="text-chariot-text-primary font-medium">
                {option.label}
              </div>
              <div className="text-chariot-text-secondary text-xs">
                {option.description}
              </div>
            </div>
            {mode === option.value && (
              <CheckIcon className="text-chariot-brand-primary size-4 flex-shrink-0" />
            )}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export default ModeSelector;
